import { create } from "zustand";
import {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Edge,
  Node,
  NodeChange,
  EdgeChange,
  MarkerType,
  Connection,
} from "reactflow";
import { v4 as uuid } from "uuid";
import { escapeBraces } from "./backend/template";
import {
  deepcopy,
  transformDict,
  APP_IS_RUNNING_LOCALLY,
  llmResponseDataToString,
} from "./backend/utils";
import { DuplicateVariableNameError } from "./backend/errors";
import {
  Dict,
  LLMGroup,
  LLMSpec,
  PromptVarType,
  TemplateVarInfo,
  TabularDataColType,
  TabularDataRowType,
  JSONCompatible,
  LLMResponse,
} from "./backend/typing";
import {
  BedrockSettings,
  WebLLMSettings,
  HuggingFaceSettings,
  OpenRouterImageSettings,
  OpenRouterSettings,
  TogetherChatSettings,
} from "./ModelSettingSchemas";
import {
  BEDROCK_PREFIX,
  TOGETHER_PREFIX,
  HUGGINGFACE_PREFIX,
  NativeLLM,
  OPENROUTER_IMAGE_PREFIX,
  OPENROUTER_PREFIX,
  OPENROUTER_EMOJI,
  openRouterEmoji,
} from "./backend/models";
import { StringLookup } from "./backend/cache";
import { saveGlobalConfig } from "./backend/backend";
import { ChunkMethodSpec } from "./ChunkMethodListComponent";
import type { RetrievalMethodSpec } from "./RetrievalMethodListComponent";
const IS_RUNNING_LOCALLY = APP_IS_RUNNING_LOCALLY();

// Initial project settings
const initialAPIKeys = {};
const initialFlags = { aiSupport: true };
const initialLLMColors = {};

/** The color palette used for displaying info about different LLMs. */
const llmColorPalette = [
  "#44d044",
  "#f1b933",
  "#e46161",
  "#8888f9",
  "#33bef0",
  "#bb55f9",
  "#f7ee45",
  "#f955cd",
  "#26e080",
  "#2654e0",
  "#7d8191",
  "#bea5d1",
];

/** The color palette used for displaying variations of prompts and prompt variables (non-LLM differences).
 * Distinct from the LLM color palette in order to avoid confusion around what the data means.
 * Palette adapted from https://lospec.com/palette-list/sness by Space Sandwich */
const varColorPalette = [
  "#0bdb52",
  "#e71861",
  "#7161de",
  "#f6d714",
  "#80bedb",
  "#ffa995",
  "#a9b399",
  "#dc6f0f",
  "#8d022e",
  "#138e7d",
  "#c6924f",
  "#885818",
  "#616b6d",
];

/** All color palettes in ChainForge. Import to use elsewhere. */
export const colorPalettes = {
  llm: llmColorPalette,
  var: varColorPalette,
};

const refreshableOutputNodeTypes = new Set([
  "evaluator",
  "processor",
  "prompt",
  "inspect",
  "vis",
  "llmeval",
  "textfields",
  "chat",
  "simpleval",
  "join",
  "split",
  "selectvars",
]);

export const initLLMProviderMenu: (LLMSpec | LLMGroup)[] = [
  {
    group: "In-browser LLMs",
    emoji: "🌐",
    items: [...prefixedMenuItems(WebLLMSettings, "", "webllm", "🌐", 0.7)],
  },
  {
    group: "OpenRouter",
    emoji: OPENROUTER_EMOJI,
    items: [
      ...prefixedMenuItems(
        OpenRouterSettings,
        OPENROUTER_PREFIX,
        "openrouter",
        openRouterEmoji,
        1.0,
      ),
      ...prefixedMenuItems(
        OpenRouterImageSettings,
        OPENROUTER_IMAGE_PREFIX,
        "openrouter-image",
        "🖼",
        0.0,
      ),
    ],
  },
  {
    group: "OpenAI",
    emoji: "🤖",
    items: [
      {
        name: "GPT-6 Astra",
        emoji: "🚀",
        model: "gpt-6-astra",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-6.1 Sol",
        emoji: "☀️",
        model: "gpt-6.1-sol",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-5.6 Terra",
        emoji: "🌍",
        model: "gpt-5.6-terra",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-6 Luna",
        emoji: "🌙",
        model: "gpt-6-luna",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-5.4 Mini",
        emoji: "🔬",
        model: "gpt-5.4-mini",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-5.4 Nano",
        emoji: "🪲",
        model: "gpt-5.4-nano",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT Image 2.5 Flare",
        emoji: "🖼",
        model: "gpt-image-2.5-flare",
        base_model: "gpt-image-1", // key of the GPT Image settings form
        temp: 0.0,
      },
      {
        name: "GPT Image 2.5 Sunburst",
        emoji: "🖼",
        model: "gpt-image-2.5-sunburst",
        base_model: "gpt-image-1",
        temp: 0.0,
      },
      {
        name: "GPT-4.1",
        emoji: "🧑‍💻️",
        model: "gpt-4.1",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-4o-mini",
        emoji: "🤖",
        model: "gpt-4o-mini",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "o3",
        emoji: "⭕",
        model: "o3",
        base_model: "gpt-4",
        temp: 1.0,
      },
      {
        name: "GPT-3.5",
        emoji: "🤖",
        model: "gpt-3.5-turbo",
        base_model: "gpt-3.5-turbo",
        temp: 1.0,
      }, // The base_model designates what settings form will be used, and must be unique.
    ],
  },
  {
    group: "Claude",
    emoji: "📚",
    items: [
      {
        name: "Claude Opus 5.5",
        emoji: "📚",
        model: "claude-opus-5-5",
        base_model: "claude-v1",
        temp: 1.0,
      },
      {
        name: "Claude Sonnet 5.5",
        emoji: "📘",
        model: "claude-sonnet-5-5",
        base_model: "claude-v1",
        temp: 1.0,
      },
      {
        name: "Claude Haiku 4.5",
        emoji: "📗",
        model: "claude-haiku-4-5",
        base_model: "claude-v1",
        temp: 1.0,
      },
      {
        name: "Claude Fable 5.1",
        emoji: "📕",
        model: "claude-fable-5-1",
        base_model: "claude-v1",
        temp: 1.0,
      },
      {
        name: "Claude Opus 4.8",
        emoji: "📙",
        model: "claude-opus-4-8",
        base_model: "claude-v1",
        temp: 1.0,
      },
      {
        name: "Claude Sonnet 4.6",
        emoji: "📓",
        model: "claude-sonnet-4-6",
        base_model: "claude-v1",
        temp: 1.0,
      },
    ],
  },
  {
    group: "Gemini",
    emoji: "♊",
    items: [
      {
        name: "Gemini 3.8 Flash",
        emoji: "⚡️",
        model: "gemini-3.8-flash",
        base_model: "gemini-2.5",
        temp: 0.7,
      },
      {
        name: "Gemini 3.7 Flash",
        emoji: "⚡️",
        model: "gemini-3.7-flash",
        base_model: "gemini-2.5",
        temp: 0.7,
      },
      {
        name: "Gemini 3.6 Flash",
        emoji: "⚡️",
        model: "gemini-3.6-flash",
        base_model: "gemini-2.5",
        temp: 0.7,
      },
      {
        name: "Gemini 3.5 Flash-Lite",
        emoji: "💨",
        model: "gemini-3.5-flash-lite",
        base_model: "gemini-2.5",
        temp: 0.7,
      },
      {
        name: "Gemini 3.1 Pro",
        emoji: "♊",
        model: "gemini-3.1-pro-preview",
        base_model: "gemini-2.5",
        temp: 0.7,
      },
      {
        name: "Gemini 3.1 Flash Image",
        emoji: "🍌",
        model: "gemini-3.1-flash-image",
        base_model: "gemini-image",
        temp: 1.0,
      },
      {
        name: "Gemini 3 Pro Image",
        emoji: "🍌",
        model: "gemini-3-pro-image",
        base_model: "gemini-image",
        temp: 1.0,
      },
    ],
  },
  {
    group: "DeepSeek",
    emoji: "🐋",
    items: [
      {
        name: "DeepSeek Flash",
        emoji: "🐋",
        model: "deepseek-flash",
        base_model: "deepseek",
        temp: 1.0,
      }, // The base_model designates what settings form will be used, and must be unique.
      {
        name: "DeepSeek V4 Pro",
        emoji: "🐳",
        model: "deepseek-v4-pro",
        base_model: "deepseek",
        temp: 1.0,
      },
    ],
  },
  {
    group: "MiniMax",
    emoji: "🔮",
    items: [
      {
        name: "MiniMax M2.7",
        emoji: "🔮",
        model: "MiniMax-M2.7",
        base_model: "minimax",
        temp: 0.7,
      },
      {
        name: "MiniMax M2.7 Highspeed",
        emoji: "⚡",
        model: "MiniMax-M2.7-highspeed",
        base_model: "minimax",
        temp: 0.7,
      },
    ],
  },
  {
    group: "HuggingFace",
    emoji: "🤗",
    items: prefixedMenuItems(
      HuggingFaceSettings,
      HUGGINGFACE_PREFIX,
      "hf",
      "🤗",
      1.0,
    ),
  },
  {
    name: "Azure OpenAI",
    emoji: "🔷",
    model: "azure-openai",
    base_model: "azure-openai",
    temp: 1.0,
  },
  {
    group: "Bedrock",
    emoji: "🪨",
    items: prefixedMenuItems(
      BedrockSettings,
      BEDROCK_PREFIX,
      "bedrock",
      "🪨",
      0.9,
    ),
  },
];

/**
 * Menu items for a provider whose models are typed in rather than enumerated:
 * one per suggested model in its settings form, so the menu and the form stay
 * in sync, each carrying the provider's prefix so ChainForge can route it back.
 */
function prefixedMenuItems(
  settings: typeof OpenRouterSettings,
  prefix: string,
  base_model: string,
  /** One emoji for every model, or one per model (e.g. by the lab behind it). */
  emoji: string | ((model: string) => string),
  temp: number,
): LLMSpec[] {
  const modelSpec = settings.schema.properties.model;
  const names = modelSpec.shortname_map as Record<string, string>;
  return (modelSpec.enum as string[]).map((model) => ({
    name: names[model] ?? model,
    emoji: typeof emoji === "function" ? emoji(model) : emoji,
    model: prefix + model,
    base_model,
    temp,
  }));
}

initLLMProviderMenu.push({
  group: "Together",
  emoji: "🤝",
  items: prefixedMenuItems(
    TogetherChatSettings,
    TOGETHER_PREFIX,
    "together",
    "🤝",
    0.9,
  ),
});

// Setup for when the app is running locally
if (IS_RUNNING_LOCALLY) {
  initLLMProviderMenu.push({
    name: "Ollama",
    emoji: "🦙",
    model: "ollama",
    base_model: "ollama",
    temp: 1.0,
  });
  // Ollama's decision models, which model menus only offer in LLM Scorers
  // (see DECISION_ONLY_BASE_MODELS). The model to ask is in its settings.
  initLLMProviderMenu.push({
    name: "Ollama (decision model)",
    emoji: "🦙",
    model: NativeLLM.Ollama_Decision,
    base_model: NativeLLM.Ollama_Decision,
    temp: 0,
  });
}

function flattenLLMGroup(group: LLMGroup): LLMSpec[] {
  return group.items.flatMap((item) =>
    "group" in item && "items" in item ? flattenLLMGroup(item) : item,
  );
}

function flattenLLMProviders(providers: (LLMSpec | LLMGroup)[]): LLMSpec[] {
  return providers.flatMap((item) =>
    "group" in item && "items" in item ? flattenLLMGroup(item) : item,
  );
}

export const initLLMProviders = flattenLLMProviders(initLLMProviderMenu);

// Favorites saved across sessions (nodes, models, etc)
export interface FavoritesStoreType {
  nodes: { name: string; value: Node; uid: string }[];
  models: { name: string; value: LLMSpec; uid: string }[];
}

export interface StoreHandles {
  // Nodes and edges
  nodes: Node[];
  edges: Edge[];

  // Helper functions for nodes and edges
  getNode: (id: string) => Node | undefined;
  addNode: (newnode: Node) => void;
  removeNode: (id: string) => void;
  deselectAllNodes: () => void;
  bringNodeToFront: (id: string) => void;
  duplicateNode: (id: string, offset?: { x?: number; y?: number }) => Node;
  setNodes: (newnodes: Node[]) => void;
  setEdges: (newedges: Edge[]) => void;
  removeEdge: (id: string) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection | Edge) => void;

  // The LLM providers available in the drop-down list
  AvailableLLMs: LLMSpec[];
  setAvailableLLMs: (specs: LLMSpec[]) => void;

  // Custom chunkers loaded via the Custom Providers dropzone
  customChunkers: ChunkMethodSpec[];
  setCustomChunkers: (chunkers: ChunkMethodSpec[]) => void;

  // Custom retrievers loaded via the Custom Providers dropzone
  customRetrievers: RetrievalMethodSpec[];
  setCustomRetrievers: (retrievers: RetrievalMethodSpec[]) => void;

  // API keys to LLM providers
  apiKeys: Dict<string>;
  setAPIKeys: (apiKeys: Dict<string>) => void;
  /** Forgets every API key set in the app. */
  clearAPIKeys: () => void;

  // The models pulled on the Ollama server, if it's running (local only)
  ollamaModels: string[];
  setOllamaModels: (models: string[]) => void;
  // The decision models (e.g. nimble) pulled on it, which LLM Scorers can use
  ollamaDecisionModels: string[];
  setOllamaDecisionModels: (models: string[]) => void;

  // Global settings (flags) from the settings menu
  globalSettings: Dict<JSONCompatible>;
  getGlobalSetting: (flag: string) => JSONCompatible;
  setGlobalSetting: (flag: string, val: JSONCompatible) => void;
  setGlobalSettings: (settings: Dict<JSONCompatible>) => void;

  // Favorites
  favorites: FavoritesStoreType;
  setFavorites: (favorites: FavoritesStoreType) => void;
  getFavorites: <K extends keyof FavoritesStoreType>(
    key: K,
  ) => FavoritesStoreType[K];
  refreshFavoriteModelsMenu: () => void;
  saveFavorite: <K extends keyof FavoritesStoreType>(
    key: K,
    name: string,
    value: FavoritesStoreType[K][number]["value"],
  ) => void;
  saveFavoriteNode: (nodeId: string, name: string) => void;
  saveFavoriteModel: (name: string, spec: LLMSpec) => void;
  removeFavorite: <K extends keyof FavoritesStoreType>(
    key: K,
    uid: string,
  ) => void;

  // Flow-specific state
  state: Dict;
  setState: (key: string, val: any) => void;
  importState: (state: Dict) => void;
  exportGradesAndNotes: (
    responses: LLMResponse[],
  ) => { grade: boolean; note?: string; response: string }[];

  // The color to represent a specific LLM, to be globally consistent
  llmColors: Dict<string>;
  getColorForLLM: (llm_name: string) => string | undefined;
  getColorForLLMAndSetIfNotFound: (llm_name: string) => string;
  genUniqueLLMColor: () => string;
  setColorForLLM: (llm_name: string, color: string) => void;
  resetLLMColors: () => void;

  // Getting inputs and outputs of nodes
  inputEdgesForNode: (sourceNodeId: string) => Edge[];
  outputEdgesForNode: (sourceNodeId: string) => Edge[];
  pingOutputNodes: (sourceNodeId: string) => void;
  getImmediateInputNodeTypes: (
    targetHandles: string[],
    node_id: string,
  ) => string[];

  // Set data for a specific node
  setDataPropsForNode: (
    id: string,
    data_props: Dict<string | boolean | number | null | Dict>,
  ) => void;

  // Rasterize data output from nodes ("pull" the data out)
  // NOTE: If targetNodeId and targetHandleKey are provided, the function will
  // delete the edge if the source node does not exist.
  output: (
    sourceNodeId: string,
    sourceHandleKey: string,
    targetNodeId?: string,
    targetHandleKey?: string,
  ) => (string | TemplateVarInfo)[] | null;
  pullInputData: (
    _targetHandles: string[],
    node_id: string,
  ) => Dict<string[] | TemplateVarInfo[]>;
}

// A global store of variables, used for maintaining state
// across ChainForge and ReactFlow components.
const useStore = create<StoreHandles>((set, get) => ({
  nodes: [],
  edges: [],

  // Available LLMs in ChainForge, in the format expected by LLMListItems.
  AvailableLLMs: [...initLLMProviders],
  setAvailableLLMs: (llmProviderList) => {
    set({ AvailableLLMs: llmProviderList });
  },

  customChunkers: [],
  setCustomChunkers: (chunkers) => {
    set({ customChunkers: chunkers });
  },

  customRetrievers: [],
  setCustomRetrievers: (retrievers: any[]) => {
    const items = (retrievers ?? [])
      .filter((p) => (p?.category ?? "retriever") === "retriever")
      .map((p) => {
        const name =
          p?.name ?? p?.methodName ?? p?.library ?? "Custom Provider";
        const baseMethod = `__custom/${name}`; // enforce canonical

        return {
          // whitelist normalized store shape
          key: p?.key ?? uuid(),
          methodName: name,
          library: name,
          baseMethod,
          emoji: p?.emoji ?? "✨",
          needsEmbeddingModel: !!p?.needs_embedding_model,
          models: Array.isArray(p?.models) ? p.models : [],
          settings_schema: p?.settings_schema ?? undefined,
          default_settings: p?.default_settings ?? undefined,
          source: "custom" as const,
        };
      });

    set({ customRetrievers: items as any });
  },

  ollamaModels: [],
  setOllamaModels: (models) => set({ ollamaModels: models }),
  ollamaDecisionModels: [],
  setOllamaDecisionModels: (models) => set({ ollamaDecisionModels: models }),

  // Keeping track of LLM API keys
  apiKeys: initialAPIKeys,
  setAPIKeys: (apiKeys) => {
    // Filter out any empty or incorrectly formatted API key values:
    const new_keys = transformDict(
      apiKeys,
      (key) =>
        (typeof apiKeys[key] === "string" && apiKeys[key].length > 0) ||
        key === "OpenAI_BaseURL",
    );
    // Only update API keys present in the new array; don't delete existing ones:
    set({ apiKeys: { ...get().apiKeys, ...new_keys } });
  },
  clearAPIKeys: () => set({ apiKeys: {} }),

  // Favorites (nodes, models, etc)
  favorites: {
    nodes: [],
    models: [],
  },
  setFavorites: (favorites: FavoritesStoreType) => {
    if (
      !favorites ||
      typeof favorites !== "object" ||
      Object.keys(favorites).length < 2
    )
      return;
    set({ favorites });
    get().refreshFavoriteModelsMenu();
  },
  getFavorites: <K extends keyof FavoritesStoreType>(key: K) => {
    return get().favorites[key];
  },
  refreshFavoriteModelsMenu: () => {
    const favorites = get().favorites;

    // Refresh the LLM menu to include the new model
    const favoritesMenuIdx = initLLMProviderMenu.findIndex(
      (item: LLMGroup | LLMSpec) =>
        "group" in item && item.group === "Favorites",
    );

    if (favorites.models.length === 0) {
      if (favoritesMenuIdx !== -1)
        // If there are no more favorites, remove the menu
        initLLMProviderMenu.splice(favoritesMenuIdx, 1);
      return;
    }

    // Otherwise, update the menu with the new favorites
    if (favoritesMenuIdx === -1) {
      initLLMProviderMenu.splice(0, 0, {
        group: "Favorites",
        emoji: "♥️",
        items: get().favorites.models.map((model) => model.value),
      });
    } else {
      (initLLMProviderMenu[favoritesMenuIdx] as LLMGroup).items =
        get().favorites.models.map((model) => model.value);
    }
  },
  saveFavorite: <K extends keyof FavoritesStoreType>(
    key: K,
    name: string,
    value: FavoritesStoreType[K][number]["value"],
  ) => {
    const favorites = { ...get().favorites };
    if (favorites[key] === undefined) favorites[key] = [];
    favorites[key].push({
      name,
      value: value as any,
      // The uid for a 'model' should be its uuid, if it's set. Otherwise, give it one:
      uid:
        key === "models" && "key" in value && value.key !== undefined
          ? value.key
          : uuid(),
    }); // add to end of array

    set({ favorites });

    // Push update to backend
    if (IS_RUNNING_LOCALLY)
      saveGlobalConfig("favorites", favorites as Dict<any>);
  },
  saveFavoriteNode: (nodeId: string, name: string) => {
    // Get the data for the node
    const node = get().getNode(nodeId);
    if (!node) return;

    // Store the node in the favorites list
    get().saveFavorite("nodes", name, node);
  },
  saveFavoriteModel: (name: string, spec: LLMSpec) => {
    // Store the model in the favorites list and refresh the menus
    get().saveFavorite("models", name, spec);
    get().refreshFavoriteModelsMenu();
  },
  removeFavorite: <K extends keyof FavoritesStoreType>(key: K, uid: string) => {
    const favorites = { ...get().favorites };
    console.log("Removing favorite", key, uid);
    const idx = favorites[key].findIndex((item) => item.uid === uid);
    if (idx !== -1) {
      favorites[key].splice(idx, 1);
    }
    set({ favorites });

    // Push update to backend
    if (IS_RUNNING_LOCALLY)
      saveGlobalConfig("favorites", favorites as Dict<any>);

    // Refresh models list if model favorite was removed
    if (key === "models") {
      get().refreshFavoriteModelsMenu();
    }
  },

  // Flags to toggle on or off features across the application
  globalSettings: initialFlags,
  getGlobalSetting: (flagName) => {
    return get().globalSettings[flagName] ?? false;
  },
  setGlobalSetting: (flagName, flagValue) => {
    const flags = { ...get().globalSettings };
    flags[flagName] = flagValue;
    set({ globalSettings: flags });
  },
  setGlobalSettings: (settings: Dict<JSONCompatible>) => {
    set({ globalSettings: settings });
  },

  // State shared across the application, for forcing redraws upon change.
  state: {},
  setState: (key, value) => {
    set((st) => ({
      ...st,
      state: {
        ...st.state,
        [key]: value,
      },
    }));
  },
  importState: (state) => {
    set((st) => ({
      ...st,
      state,
    }));
  },
  exportGradesAndNotes: (responses: LLMResponse[]) => {
    const state = get().state;
    const res: { grade: boolean; note?: string; response: string }[] = [];
    responses.forEach((r) => {
      const uid = r.uid;
      if (r.uid === undefined || r.responses?.length === 0) return;
      const gradeKey = `r.${uid}.grade`;
      const noteKey = `r.${uid}.note`;
      const grade = state[gradeKey];
      const note = state[noteKey];
      if (grade === undefined) return;
      res.push({
        grade: grade?.[0],
        note: note?.[0],
        response: llmResponseDataToString(r.responses[0]),
      }); // TODO: support multiple responses when n>1
    });
    return res;
  },

  // Keep track of LLM colors, to ensure color consistency across various plots and displays
  llmColors: initialLLMColors,

  // Gets the color for the model named 'llm_name' in llmColors; returns undefined if not found.
  getColorForLLM: (llm_name) => {
    const colors = get().llmColors;
    if (llm_name in colors) return colors[llm_name];
    return undefined;
  },

  // Gets the color for the specified LLM. If not found, generates a new (ideally unique) color
  // and saves it to the llmColors dict.
  getColorForLLMAndSetIfNotFound: (llm_name) => {
    let color = get().getColorForLLM(llm_name);
    if (color) return color;
    color = get().genUniqueLLMColor();
    get().setColorForLLM(llm_name, color);
    return color;
  },

  // Generates a unique color not yet used in llmColors (unless # colors is large)
  genUniqueLLMColor: () => {
    const used_colors = new Set(Object.values(get().llmColors));
    const get_unused_color = (all_colors: string[]) => {
      for (let i = 0; i < all_colors.length; i++) {
        const color = all_colors[i];
        if (!used_colors.has(color)) return color;
      }
      return undefined;
    };

    let unique_color = get_unused_color(llmColorPalette);
    if (unique_color) return unique_color;

    // If we've reached here, we've exhausted all colors in the LLM palette. As a backup,
    // we'll use the color palette for vars, and try that:
    unique_color = get_unused_color(varColorPalette);
    if (unique_color) return unique_color;

    // If we've reached here, we've run out of all predefined colors.
    // Choose one to repeat, at random:
    const all_colors = llmColorPalette.concat(varColorPalette);
    return all_colors[Math.floor(Math.random() * all_colors.length)];
  },

  // Saves the color for the specified LLM
  setColorForLLM: (llm_name, color) => {
    get().llmColors[llm_name] = color;
  },

  // Resets (removes) all LLM colors
  resetLLMColors: () => {
    set({
      llmColors: {},
    });
  },

  inputEdgesForNode: (sourceNodeId) => {
    return get().edges.filter((e) => e.target === sourceNodeId);
  },
  outputEdgesForNode: (sourceNodeId) => {
    return get().edges.filter((e) => e.source === sourceNodeId);
  },
  pingOutputNodes: (sourceNodeId) => {
    const out_nodes = get()
      .outputEdgesForNode(sourceNodeId)
      .map((e) => e.target);
    out_nodes.forEach((n) => {
      const node = get().getNode(n);
      if (
        node?.type !== undefined &&
        refreshableOutputNodeTypes.has(node.type)
      ) {
        get().setDataPropsForNode(node.id, { refresh: true });
      }
    });
  },
  output: (sourceNodeId, sourceHandleKey, targetNodeId, targetHandleKey) => {
    // Get the source node
    const src_node = get().getNode(sourceNodeId);
    if (!src_node) {
      console.error("Could not find node with id", sourceNodeId);
      if (targetNodeId !== undefined && targetHandleKey !== undefined) {
        // If the source node doesn't exist, delete the edge if it exists
        const edges = get().edges.filter(
          (e) =>
            e.source === sourceNodeId &&
            e.target === targetNodeId &&
            e.targetHandle === targetHandleKey,
        );
        if (edges.length > 0) {
          console.warn("Removing invalid edge...");
          edges.forEach((e) => {
            get().removeEdge(e.id);
          });
        }
      }
      return null;
    }

    // If the source node has tabular data, use that:
    if (src_node.type === "table" || src_node.type === "media") {
      if (
        ("sel_rows" in src_node.data || "rows" in src_node.data) &&
        "columns" in src_node.data
      ) {
        const rows: TabularDataRowType[] =
          src_node.data.sel_rows ?? src_node.data.rows;
        const columns: TabularDataColType[] = src_node.data.columns;

        // The sourceHandleKey is the key of the column in the table that we're interested in:
        const src_col = columns.find((c) => c.header === sourceHandleKey);
        if (src_col !== undefined) {
          // Construct a lookup table from column key to header name,
          // as the 'metavars' dict should be keyed by column *header*, not internal key:
          const col_header_lookup: Dict<string> = {};
          columns.forEach((c) => {
            col_header_lookup[c.key] = c.header;
          });

          // Extract all the data for every row of the source column, appending the other values as 'meta-vars':
          return rows
            .map((row) => {
              const row_keys = Object.keys(row);

              // Check if this is an 'empty' row (with all empty strings); if so, skip it:
              if (
                row_keys.every(
                  (key) =>
                    key === "__uid" ||
                    !row[key] ||
                    ((typeof row[key] === "string" ||
                      typeof row[key] === "number") &&
                      StringLookup.get(row[key])?.trim() === ""),
                )
              )
                return undefined;

              const row_excluding_col: Dict<string> = {};
              row_keys.forEach((key) => {
                if (key !== src_col.key && key !== "__uid")
                  row_excluding_col[col_header_lookup[key]] = (
                    StringLookup.get(row[key]) ?? "(string lookup failed)"
                  ).toString();
              });
              const key_name =
                src_node.type === "media" && sourceHandleKey === "Image"
                  ? "image"
                  : "text";
              return {
                // We escape any braces in the source text before they're passed downstream.
                // This is a special property of tabular data nodes: we don't want their text to be treated as prompt templates.
                [key_name]: escapeBraces(
                  src_col.key in row
                    ? (
                        StringLookup.get(row[src_col.key]) ??
                        "(string lookup failed)"
                      ).toString()
                    : "",
                ),
                metavars: row_excluding_col,
                associate_id: row.__uid, // this is used by the backend to 'carry' certain values together
              };
            })
            .filter((r) => r !== undefined);
        } else {
          console.error(
            `Could not find table column with source handle name ${sourceHandleKey}`,
          );
          return null;
        }
      }
    } else {
      // Get the data related to that handle:
      if ("fields" in src_node.data) {
        if (Array.isArray(src_node.data.fields)) return src_node.data.fields;
        else {
          // We have to filter over a special 'fields_visibility' prop, which
          // can select what fields get output:
          if ("fields_visibility" in src_node.data)
            return Object.values(
              transformDict(
                src_node.data.fields,
                // eslint-disable-next-line
                (fid) => src_node.data.fields_visibility[fid] !== false,
                undefined,
                undefined,
              ),
            );
          // return all field values
          else
            return Object.values(
              transformDict(
                src_node.data.fields,
                undefined,
                undefined,
                undefined,
              ),
            );
        }
      }
      // NOTE: This assumes it's on the 'data' prop, with the same id as the handle:
      else return src_node.data[sourceHandleKey];
    }
  },

  // Get the types of nodes attached immediately as input to the given node
  getImmediateInputNodeTypes: (_targetHandles, node_id) => {
    const getNode = get().getNode;
    const edges = get().edges;
    const inputNodeTypes: string[] = [];
    edges.forEach((e) => {
      if (
        e.target === node_id &&
        typeof e.targetHandle === "string" &&
        _targetHandles.includes(e.targetHandle)
      ) {
        const src_node = getNode(e.source);
        if (src_node && src_node.type !== undefined)
          inputNodeTypes.push(src_node.type);
      }
    });
    return inputNodeTypes;
  },

  // Pull all inputs needed to request responses.
  // Returns [prompt, vars dict]
  pullInputData: (_targetHandles, node_id) => {
    // Functions/data from the store:
    const getNode = get().getNode;
    const output = get().output;
    const edges = get().edges;

    // Helper function to store collected data in dict:
    const store_data = (
      _texts: PromptVarType[],
      _varname: string,
      _data: Dict<PromptVarType[]>,
    ) => {
      if (_varname in _data) _data[_varname] = _data[_varname].concat(_texts);
      else _data[_varname] = _texts;
    };

    // Pull data from each source recursively:
    const pulled_data = {};
    const get_outputs = (
      varnames: string[],
      nodeId: string,
      var_history: Set<string>,
    ) => {
      varnames.forEach((varname) => {
        // Check for duplicate variable names
        if (var_history.has(String(varname).toLowerCase()))
          throw new DuplicateVariableNameError(varname);

        // Add to unique name tally
        var_history.add(String(varname).toLowerCase());

        // Find the relevant edge(s):
        edges.forEach((e) => {
          if (e.target === nodeId && e.targetHandle === varname) {
            // Get the immediate output:
            const out =
              e.sourceHandle != null
                ? output(e.source, e.sourceHandle, e.target, e.targetHandle)
                : undefined;
            if (!out || !Array.isArray(out) || out.length === 0) return;

            // Check the format of the output. Can be str or dict with 'text' and more attrs:
            if (typeof out[0] === "object") {
              out.forEach((obj) => store_data([obj], varname, pulled_data));
            } else {
              // Save the list of strings from the pulled output under the var 'varname'
              store_data(out, varname, pulled_data);
            }

            // Get any vars that the output depends on, and recursively collect those outputs as well:
            const n_vars = getNode(e.source)?.data?.vars;
            if (n_vars && Array.isArray(n_vars) && n_vars.length > 0)
              get_outputs(n_vars, e.source, var_history);
          }
        });
      });
    };
    get_outputs(_targetHandles, node_id, new Set<string>());

    return pulled_data;
  },

  /**
   * Sets select 'data' properties for node 'id'. This updates global state, and forces re-renders. Use sparingly.
   * @param {*} id The id of the node to set 'data' properties for.
   * @param {*} data_props The properties to set on the node's 'data'.
   */
  setDataPropsForNode: (id: string, data_props: Dict) => {
    const _set = (nds: Node[]) =>
      nds.map((n) => {
        if (n.id === id) {
          for (const key of Object.keys(data_props))
            n.data[key] = data_props[key];
          n.data = deepcopy(n.data);
        }
        return n;
      });
    set({
      nodes: _set(get().nodes),
    });
  },
  getNode: (id) => get().nodes.find((n) => n.id === id),
  addNode: (newnode) => {
    // Make sure we select the added node.
    // This will float it to the top.
    get().deselectAllNodes();
    newnode.selected = true;

    // Add the node to the internal state
    set({
      nodes: get().nodes.concat(newnode),
    });
  },
  removeNode: (id) => {
    set({
      nodes: get().nodes.filter((n) => n.id !== id),
    });
  },
  deselectAllNodes: () => {
    // Deselect all nodes
    set({
      nodes: get().nodes.map((n) => {
        n.selected = false;
        return n;
      }),
    });
  },
  bringNodeToFront: (id) => {
    set({
      nodes: get().nodes.map((n) => {
        n.selected = n.id === id;
        return n;
      }),
    });
  },
  duplicateNode: (id, offset) => {
    const nodes = get().nodes;
    const node = nodes.find((n) => n.id === id);
    if (!node) {
      // console.error(`Could not duplicate node: No node found with id ${id}`);
      return undefined;
    }
    // Deep copy node data
    const dup = JSON.parse(JSON.stringify(node));
    // Shift position
    dup.position.x += offset && offset.x !== undefined ? offset.x : 0;
    dup.position.y += offset && offset.y !== undefined ? offset.y : 0;
    // Change id to new unique id
    dup.id = `${dup.type}-${Date.now()}`;
    // Return the duplicated node (does not add it to ReactFlow nodes; use addNode for that!)
    return dup;
  },
  setNodes: (newnodes) => {
    set({
      nodes: newnodes,
    });
  },
  setEdges: (newedges) => {
    set({
      edges: newedges,
    });
  },
  removeEdge: (id) => {
    set({
      edges: applyEdgeChanges([{ id, type: "remove" }], get().edges),
    });
  },
  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },
  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },
  onConnect: (connection) => {
    // Get the target node information
    const target = connection.target
      ? get().getNode(connection.target)
      : undefined;
    if (target === undefined) return;

    if (
      target.type === "vis" ||
      target.type === "inspect" ||
      target.type === "simpleval"
    ) {
      get().setDataPropsForNode(target.id, { input: connection.source });
    }

    // Ping target node to fresh if necessary
    if (
      typeof target?.type === "string" &&
      refreshableOutputNodeTypes.has(target.type)
    ) {
      get().setDataPropsForNode(target.id, { refresh: true });
    }

    connection = connection as Edge;
    connection.interactionWidth = 40;
    connection.animated = true;
    connection.markerEnd = { type: MarkerType.Arrow, width: 22, height: 22 }; // 22px
    connection.type = "default";

    set({
      edges: addEdge(connection, get().edges), // get().edges.concat(connection)
    });
  },
}));

export default useStore;
